import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { OnboardingStep, TUpdateProfile, TUser } from "@/models/user.model";
import authService from "@services/auth.service";
import { TRequestStatus, TStep1Data } from "@models/msc.model";
import { TLoginPayload } from "@models/auth.model";
import {
  getAuthToken,
  removeAuthToken,
  setAuthProvider,
  setAuthToken,
  setRefreshToken,
} from "@utils/axios-instance";
import meService from "@/services/me.service";
import metaService from "@/services/meta.service";
import googleAdsService, { GoogleAdsAccount } from "@/services/google.ads.service";
import onboardingService from "@/services/onboarding.service";
import { TMetaAdAccount } from "@/models/meta.model";
import { TMyCallCenterPreferences } from "@/models/call.center.model";

const TOTAL_ONBOARDING_STEPS = 5;

const initialUser: TUser = {
  id: 0,
  status: 0,
  companyUniqueId: "",
  companyUniqueName: "",
  email: "",
  firstName: "",
  lastName: "",
  timezone: "",
  availability: [],
  avatar: null,
  roles: [],
  teams: [],
  created: "",
  updated: "",
  defaultCompanyDomain: "",
};

type AuthState = {
  user: TUser;
  initialized: boolean;
  isLoggedIn: boolean;
  status: TRequestStatus;
  accessToken: string | null;
};

type AuthActions = {
  initializeApp: () => void;
  login: (data: TLoginPayload) => Promise<void>;
  getProfile: () => Promise<void>;
  logout: () => void;
  userLoggedIn: () => void;
  hubspotAccountDisconnected: () => void;
  userAvailabilitySaved: (payload: Pick<TUser, "availability" | "timezone">) => void;
  updateProfile: (profile: Omit<TUpdateProfile, "email">) => Promise<unknown>;
  updateMyCallCenterPreferences: (
    preferences: Partial<TMyCallCenterPreferences>
  ) => Promise<unknown>;
  updateOnboarding: (data: { currentStep: OnboardingStep }) => Promise<unknown>;
  updateLocalMe: (data: Partial<TUser>) => void;
  // CALL CENTER ONBOARDING
  skipStep: (step: number) => Promise<void>;
  submitStep1: (data: TStep1Data) => Promise<void>;
  submitStep2: () => Promise<void>;
  submitStep3: (file: File) => Promise<void>;
  submitStep4: (file: File) => Promise<void>;
  submitStep5: (file: File) => Promise<void>;
  // INTEGRATIONS
  disconnectMetaAccount: () => Promise<void>;
  updateMetaAccount: (account: TMetaAdAccount) => Promise<void>;
  disconnectGoogleAdsAccount: () => Promise<void>;
  updateGoogleAdsAccount: (account: GoogleAdsAccount) => Promise<void>;
};

// The old reducer merged onboarding payloads untyped (currentStep travels as an
// OnboardingStep key but is stored alongside the numeric step) — keep that shape.
const mergeOnboarding = (user: TUser, payload: unknown): TUser => ({
  ...user,
  onboarding: {
    ...user.onboarding,
    ...(payload as Partial<TUser["onboarding"]>),
  } as TUser["onboarding"],
});

const advanceOnboardingStep = (user: TUser): TUser => {
  if (!user.onboarding) return user;
  const newStep = user.onboarding.currentStep + 1;
  if (newStep >= TOTAL_ONBOARDING_STEPS) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { onboarding: _done, ...rest } = user;
    return rest as TUser;
  }
  return { ...user, onboarding: { ...user.onboarding, currentStep: newStep } };
};

export const useAuthStore = create<AuthState & AuthActions>()(
  devtools(
    (set, get) => ({
      user: initialUser,
      initialized: false,
      isLoggedIn: getAuthToken() !== null,
      status: "IDLE",
      accessToken: null,

      initializeApp: () => {
        if (getAuthToken() === null) {
          set({ initialized: true }, false, "initializeApp");
          return;
        }
        setAuthToken();
      },

      login: async (data) => {
        set({ status: "PENDING" }, false, "login/pending");
        try {
          const payload = await authService.login(data);

          // inject token to axios instance
          const { AccessToken, RefreshToken, AuthProvider } = payload;
          setAuthToken(AccessToken);
          setRefreshToken(RefreshToken);
          setAuthProvider(AuthProvider);

          set(
            { accessToken: AccessToken, status: "IDLE", isLoggedIn: true },
            false,
            "login/fulfilled"
          );
        } catch (error) {
          set({ status: "REJECTED" }, false, "login/rejected");
          throw error;
        }
      },

      getProfile: async () => {
        try {
          const payload = await meService.getMe();
          set(
            { user: payload, initialized: true, isLoggedIn: true },
            false,
            "getProfile/fulfilled"
          );
        } catch {
          removeAuthToken();
          set({ isLoggedIn: false, initialized: true }, false, "getProfile/rejected");
        }
      },

      logout: () => {
        removeAuthToken();
        set(
          { user: initialUser, isLoggedIn: false, accessToken: null },
          false,
          "logout"
        );
      },

      userLoggedIn: () => set({ isLoggedIn: true }, false, "userLoggedIn"),

      hubspotAccountDisconnected: () =>
        set(
          (state) => ({ user: { ...state.user, hubspotConnected: false } }),
          false,
          "hubspotAccountDisconnected"
        ),

      userAvailabilitySaved: ({ availability, timezone }) =>
        set(
          (state) => ({ user: { ...state.user, availability, timezone } }),
          false,
          "userAvailabilitySaved"
        ),

      updateProfile: async (profile) => {
        const response = await meService.updateProfile(profile);
        set(
          (state) => ({ user: { ...state.user, ...profile } }),
          false,
          "updateProfile/fulfilled"
        );
        return response;
      },

      updateMyCallCenterPreferences: async (preferences) => {
        const response = await meService.updateMyCallCenterPreferences(preferences);
        set(
          (state) => ({ user: { ...state.user, ...preferences } }),
          false,
          "updateMyCallCenterPreferences/fulfilled"
        );
        return response;
      },

      // Optimistic update with rollback to the previous onboarding snapshot.
      updateOnboarding: async (data) => {
        const currentOnboarding = get().user?.onboarding;
        if (currentOnboarding) {
          set(
            (state) => ({ user: mergeOnboarding(state.user, { ...currentOnboarding, ...data }) }),
            false,
            "updateOnboarding/optimistic"
          );
        }

        try {
          const response = await onboardingService.updateOnboarding(data);
          set(
            (state) => ({ user: mergeOnboarding(state.user, response) }),
            false,
            "updateOnboarding/fulfilled"
          );
          return response;
        } catch (err) {
          set(
            (state) => ({ user: mergeOnboarding(state.user, currentOnboarding) }),
            false,
            "updateOnboarding/rollback"
          );
          throw err;
        }
      },

      updateLocalMe: (data) =>
        set((state) => ({ user: { ...state.user, ...data } }), false, "updateLocalMe"),

      // CALL CENTER ONBOARDING
      skipStep: async (step) => {
        await onboardingService.skipStep(step);
        set((state) => ({ user: advanceOnboardingStep(state.user) }), false, "stepSkipped");
      },

      submitStep1: async (data) => {
        await onboardingService.submitStep1(data);
        set((state) => ({ user: advanceOnboardingStep(state.user) }), false, "stepSubmitted");
      },

      submitStep2: async () => {
        set((state) => ({ user: advanceOnboardingStep(state.user) }), false, "stepSubmitted");
      },

      submitStep3: async (file) => {
        await onboardingService.submitStep3(file);
        set((state) => ({ user: advanceOnboardingStep(state.user) }), false, "stepSubmitted");
      },

      submitStep4: async (file) => {
        await onboardingService.submitStep4(file);
        set((state) => ({ user: advanceOnboardingStep(state.user) }), false, "stepSubmitted");
      },

      submitStep5: async (file) => {
        await onboardingService.submitStep5(file);
        set((state) => ({ user: advanceOnboardingStep(state.user) }), false, "stepSubmitted");
      },

      // INTEGRATIONS
      disconnectMetaAccount: async () => {
        await metaService.disconnectMetaAccount();
        set(
          (state) => ({
            user: {
              ...state.user,
              metaConnected: false,
              metaClientId: undefined,
              metaClientName: undefined,
            },
          }),
          false,
          "metaAccountDisconnected"
        );
      },

      updateMetaAccount: async (account) => {
        await metaService.updateCurrentAdAccount(account);
        set(
          (state) => ({
            user: { ...state.user, metaClientId: account.id, metaClientName: account.name },
          }),
          false,
          "metaAccountUpdated"
        );
      },

      disconnectGoogleAdsAccount: async () => {
        await googleAdsService.disconnectGoogleAdsAccount();
        set(
          (state) => ({
            user: {
              ...state.user,
              googleAdsConnected: false,
              googleAdClientId: undefined,
              googleAdClientName: undefined,
            },
          }),
          false,
          "googleAdsAccountDisconnected"
        );
      },

      updateGoogleAdsAccount: async (account) => {
        await googleAdsService.updateCurrentAdAccount(account);
        set(
          (state) => ({
            user: { ...state.user, googleAdClientId: account.id, googleAdClientName: account.name },
          }),
          false,
          "googleAdsAccountUpdated"
        );
      },
    }),
    { name: "auth" }
  )
);
